import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo
} from 'react';
import { Feature as GeoJSONFeature, Geometry } from 'geojson';
import { PathOptions, LeafletEventHandlerFn } from 'leaflet';
import { GeoJSON } from 'react-leaflet';
import { useDebouncedCallback } from 'use-debounce';

import { Format, Karte, GeoData, GeoDataProperties, Values } from '../../types';
import {
  getRange,
  getSimilarityLegendColor,
  getLegendColor,
  getAreaName
} from '../../util';

export type Props = {
  data?: GeoData;
  values?: Values;
  karte?: Karte;
  similarityAreaNameList?: string[];
  onAreaSelect?: (areaName?: string) => void;
};

const Polygons: React.FC<Props> = ({
  data,
  values,
  karte,
  similarityAreaNameList,
  onAreaSelect
}) => {
  const ref = useRef<GeoJSON>(null);
  const [selected, select] = useState<string>();
  const [handleSelect] = useDebouncedCallback((area?: string) => {
    select(area);
    onAreaSelect?.(area);
  }, 100);
  const [dataKey, setDataKey] = useState(0);

  const range = useMemo(() => {
    if (!karte?.populationDataType) return undefined;
    return getRange(karte.category, karte.populationDataType, karte.valueType);
  }, [karte]);

  const getColor = useCallback(
    (value: number) => {
      if (!range || karte?.format === Format.Similarity) {
        return getSimilarityLegendColor(value);
      }
      if (value > range[3]) {
        return getLegendColor(4);
      } else if (value > range[2]) {
        return getLegendColor(3);
      } else if (value > range[1]) {
        return getLegendColor(2);
      } else if (value > range[0]) {
        return getLegendColor(1);
      } else {
        return getLegendColor(0);
      }
    },
    [karte, range]
  );

  const style = useCallback(
    (feature?: GeoJSONFeature<Geometry, GeoDataProperties>): PathOptions => {
      if (!feature || !karte || !values) {
        return {};
      }

      const areaName = getAreaName(feature.properties, karte.category);
      if (!areaName) return {};

      const isSelected = (selected ?? karte.area) === areaName;

      // そのエリアが類似地域地域名リストに入っているかどうか判定する。
      const isAreaNameInSimilarityList =
        karte.format === Format.Similarity &&
        similarityAreaNameList?.includes(areaName);

      return {
        fillColor: values[areaName] ? getColor(values[areaName]) : '',
        opacity: 0.9,
        color: '#800026',
        weight: isSelected
          ? 4 //選択エリアの場合
          : isAreaNameInSimilarityList
          ? 2 //選択エリアではないが、類似エリアの場合
          : 1, //そのどれでもない場合
        dashArray: isSelected || isAreaNameInSimilarityList ? '' : '3',
        fillOpacity: isSelected ? 0.9 : 0.7
      };
    },
    [getColor, karte, selected, similarityAreaNameList, values]
  );

  const first = useRef(false);
  useEffect(() => {
    if (first.current) {
      select(undefined);
      setDataKey(i => (i + 1) % 100); // force re-render
    }
    first.current = true;
  }, [data]);

  useEffect(() => {
    const layers = ref.current?.leafletElement?.getLayers();
    if (!karte || !layers) return;

    const mouseOverEvents: (LeafletEventHandlerFn | undefined)[] = [];
    const mouseOutEvents: (LeafletEventHandlerFn | undefined)[] = [];

    layers.forEach(layer => {
      const areaName = getAreaName(
        ((layer as any).feature as GeoJSONFeature<Geometry, GeoDataProperties>)
          .properties,
        karte.category
      );

      const mouseOver = areaName ? () => handleSelect(areaName) : undefined;
      const mouseOut = areaName ? () => handleSelect() : undefined;

      if (mouseOver) {
        layer.on('mouseover', mouseOver);
      }
      if (mouseOut) {
        layer.on('mouseout', mouseOut);
      }

      mouseOverEvents.push(mouseOver);
      mouseOutEvents.push(mouseOut);
    });

    return () => {
      layers.forEach((layer, i) => {
        layer.off('mouseover', mouseOverEvents[i]);
        layer.off('mouseout', mouseOutEvents[i]);
      });
    };
  }, [karte, handleSelect, dataKey]);

  return data ? (
    <GeoJSON ref={ref} data={data} style={style} key={dataKey} />
  ) : null;
};

export default Polygons;
